/**
 * Shared content constants for the platform apex pages (Landing, Mission,
 * About, and the CitationFooter they all render).
 *
 * These are the building blocks more than one apex page depends on, defined
 * once here so the pages import a single source of truth rather than each
 * carrying their own copy. Page-specific content (e.g. the RILE acronym cards,
 * which only the Landing page renders) stays co-located in its page component,
 * not here — this file is intentionally limited to genuinely shared data so it
 * does not become a grab-bag.
 *
 * Provenance: the substance is drawn from the working whitepaper "Foundation of
 * a Reciprocal Integrated Learning Ecosystem (RILE): Learning from the Ground
 * Up" (Johnson, Kumin, Rajan, CC BY-SA 4.0). The CitationFooter carries the
 * required attribution; see `working-notes/costem-whitepaper-notes.md` for
 * source provenance.
 */

// The whitepaper's public home. Linked from the CitationFooter (every apex
// page) and from the Landing page's "What is RILE?" prose. CC BY-SA 4.0
// attribution is required when paraphrasing or quoting from it.
export const WHITEPAPER_WIKI_URL =
    'https://coloradostemecosystem.miraheze.org/wiki/Main_Page';

// The whitepaper's persistent identifier (Crossref). The DOI resolves to a
// pinned wiki revision, so it is the citable form; the wiki URL above stays
// the "read the live document" link. Every formal citation carries both.
export const WHITEPAPER_DOI = '10.25261/cstem124';
export const WHITEPAPER_DOI_URL = `https://doi.org/${WHITEPAPER_DOI}`;

// The department's page for the lab, not the lab's own site (c3-lab.org, which
// `Landing` links). This is the institutional page, which is the right target
// where the surrounding sentence names MSU Denver's department.
export const C3_LAB_URL =
    'https://www.msudenver.edu/department-of-computer-sciences/c3-lab/';

// The five design pillars from the project vision. `Landing` renders the
// `short` form; `Mission` renders the `long` form. Both read from this single
// list so the pillar set cannot drift between the two pages. `ComingSoon`
// (routed separately at `/coming-soon`, #205/#353) has its own distinct
// short-form pillar copy matching the approved Figma design (PILLAR_CARDS in
// ComingSoon.jsx), deliberately not sourced from this list, since it's
// temporary placeholder copy rather than the platform's real pillar
// description.
export const DESIGN_PILLARS = [
    {
        name: 'Transparency',
        short: 'Ecosystem visible by default. System logic explainable.',
        long:
            'An ecosystem is only useful if you can see it. RILE Connect ' +
            'surfaces the network of who is doing what so connections are ' +
            'discoverable, not hidden behind closed lists. System behavior ' +
            'is explainable rather than algorithmically opaque.',
    },
    {
        name: 'Trust',
        short: 'Minimal data collection. Clear governance. Predictable behavior.',
        long:
            'Members decide what they share. The platform collects the ' +
            'minimum data needed to make the ecosystem function, governs it ' +
            'transparently, and behaves predictably so participation is a ' +
            'safe default rather than a leap of faith.',
    },
    {
        name: 'Accessibility',
        short: 'WCAG 2.1 AA baseline. Mobile-first for rural users.',
        long:
            'Accessibility is a baseline, not a polish step. The platform ' +
            'targets WCAG 2.1 AA conformance, designs mobile-first for ' +
            'participants in rural or under-served areas, and treats device ' +
            'and bandwidth constraints as first-class design inputs.',
    },
    {
        name: 'Equity',
        short: "Designed for first-time users. Analytics focused on who's missing.",
        long:
            'The platform is designed for the participant who has never ' +
            'attended a conference, never had a mentor, and does not know the ' +
            'vocabulary. Analytics ask who is absent from the ecosystem, not ' +
            'just who is engaging with it, because the absences are the data ' +
            'that matters most.',
    },
    {
        name: 'Replicability',
        short: 'Open-source, modular, documented for other regions.',
        long:
            'The whitepaper explicitly called for "systems that can be ' +
            'repurposed by other Ecosystems." RILE Connect is open source, ' +
            'modular, and documented so any region or topical community can ' +
            'stand up its own ecosystem rather than depend on a single ' +
            'central operator.',
    },
];
